/**
 * Self-service password change, reused verbatim across the staff gear menu,
 * the owner/tenant portal's AccountMenu, and the applicant flow's account
 * menu. No props, no actor-type awareness — Better Auth's `/change-password`
 * endpoint is scoped entirely by the caller's own session, so this component
 * doesn't need to know or care who's using it. See CLAUDE.md's admin-
 * immutability note for why that includes the ADMIN account: this endpoint
 * runs on systemPrisma/app_migrator, never through app_user/RLS, and it
 * requires the current password, so RLS was never what gated it.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { api } from '../lib/api.js';
import { Field, FormErrors, PasswordInput } from './Form.js';

export function ChangePasswordForm() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setDone(false);

    if (newPassword !== confirmPassword) {
      setError(new Error('Passwords do not match.'));
      return;
    }

    setBusy(true);
    try {
      // revokeOtherSessions is hardcoded true, not a checkbox — it's the
      // safeguard that kills any concurrently-held attacker session the
      // moment the legitimate user changes their password, not an option.
      await api('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword, newPassword, revokeOtherSessions: true }),
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setDone(true);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="stack" onSubmit={(event) => void onSubmit(event)}>
      <Field label="Current password" name="currentPassword" required>
        <PasswordInput
          name="currentPassword"
          value={currentPassword}
          onChange={setCurrentPassword}
          autoComplete="current-password"
          required
        />
      </Field>
      <Field label="New password" name="newPassword" hint="At least 12 characters." required>
        <PasswordInput
          name="newPassword"
          value={newPassword}
          onChange={setNewPassword}
          autoComplete="new-password"
          required
        />
      </Field>
      <Field label="Confirm new password" name="confirmPassword" required>
        <PasswordInput
          name="confirmPassword"
          value={confirmPassword}
          onChange={setConfirmPassword}
          autoComplete="new-password"
          required
        />
      </Field>

      <button type="submit" className="button" disabled={busy}>
        {busy ? 'Updating…' : 'Update password'}
      </button>
      {error ? <FormErrors error={error} /> : null}
      {done ? <p className="muted">Password updated.</p> : null}
    </form>
  );
}
